import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, Subject } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { getLikelihoodBandLabel, getLikelihoodColor, getRiskColor } from 'src/app/_helpers/risk-color.helper';
import { LikelihoodColorLevel, RiskLevel } from 'src/app/models/risk-assessment.model';
import {
    SamplingPlanMatrix,
    SamplingPlanMatrixCell,
    SamplingPlanMatrixColumn,
    SamplingPlanMatrixGroupContribution,
    SamplingPlanMatrixSeg,
    SamplingPlanMatrixWriteCell,
} from 'src/app/models/samplingPlanMatrix.model';
import { SamplingPlan } from 'src/app/models/samplingPlan.model';
import { MethodOfAssessment } from 'src/app/enums/methodOfAssessment.enum';
import { SamplingPlanStatus } from 'src/app/enums/samplingPlanStatus.enum';
import { PermissionService } from 'src/app/services/permission.service';
import { PermissionFlags } from 'src/app/enums/permissionFlags.enum';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';
import { CommonService } from 'src/app/services/common.service';
import { JurisdictionRiskConfigService } from 'src/app/services/Jurisdiction-risk-config.service';
import { SamplingPlanService } from 'src/app/services/samplingPlan.service';
import { TenantRiskService } from 'src/app/services/tenantRisk.service';
import { ToastService } from 'src/app/services/toast.service';
import { TranslateService } from 'src/app/services/translate.service';
import { resolveRiskConfig } from 'src/app/shared/utils/risk-config-resolution.util';
import { RoutePaths } from 'src/app/shared/constants/routePaths.constants';
import { ConfirmDialogComponent, ConfirmDialogData } from 'src/app/shared/confirmDialog/confirmDialog.component';

// DASH-2706 (G11v2): pre-computed per-cell view-model so the nested SEG x column template iterates plain property
// reads instead of re-running getCell/getCellColor/isPlannable/isOverridden/getPlanned on every change-detection
// cycle. Rebuilt in setMatrix()/filterColumns()/toggleColoringMode() and patched in place on override/planned edits.
interface CellViewModel {
    seg: SamplingPlanMatrixSeg;
    column: SamplingPlanMatrixColumn;
    cell: SamplingPlanMatrixCell | undefined;
    color: string;
    notAssessed: boolean;
    isHazardNa: boolean;
    isOverridden: boolean;
    isQuant: boolean;
    isQual: boolean;
    bandLabel: string | null;
    plannable: boolean;
    planned: number | null;
    showEnable: boolean;
    // DASH-2706 filters (item 3): true when the cell does not match the active filters — dimmed + non-interactive.
    filteredOut: boolean;
    // DASH-2706 Go-Bulk (item 2): per-cell selection marker (only meaningful in bulk mode).
    selected: boolean;
    // DASH-2706 group-rollup (item 4): true for a group column (its Planned # is a DIRECT group value).
    isGroupCol: boolean;
    // DASH-2709: true for an AGENT cell that inherits Planned # from one or more Agent-Groups — renders the editable
    // DIRECT value + read-only LOCKED group-contribution chip + computed TOTAL, with the per-group breakdown on hover.
    isRollup: boolean;
    contribution: number;
    total: number;
    rollupTitle: string;
}

interface RowViewModel {
    seg: SamplingPlanMatrixSeg;
    cells: CellViewModel[];
    // DASH-2706 Go-Bulk: header row-select reflects "all plannable cells in the row selected".
    selected: boolean;
}

// DASH-2712 AC5: one row of the SEG Profile Summary rollup — a per-column Planned rollup summarised across ALL SEGs.
// For an AGENT column: DIRECT (sum of its own Planned) + CONTRIBUTION (sum inherited from the Agent-Group(s) it belongs
// to, via the DASH-2709 groupContribution) => TOTAL, annotated group-agnostically ('incl. N from group(s)') with the
// per-group names on hover only. For a GROUP column: its own DIRECT total, kept as a SEPARATE line (never summed into a
// member agent's row).
interface PlannedRollupRow {
    columnName: string;
    isGroup: boolean;
    direct: number;
    contribution: number;
    total: number;
    // group-agnostic visible annotation ('incl. N from group(s)'); '' when nothing is inherited.
    contributionLabel: string;
    // per-group breakdown with names, shown on hover only ('incl. N from {group}, M from {group2}').
    rollupTitle: string;
}

// DASH-2706 filters (item 3): a matrix filter option. Options in the same `group` combine with OR; across groups AND.
interface MatrixFilterOption {
    id: string;
    label: string;
    group: 'method' | 'state';
    test: (cell: SamplingPlanMatrixCell | undefined) => boolean;
}

// DASH-2706 (G11v2): the plan-DETAIL matrix (SEG rows x agent/agent-group columns) reached from the Sampling Plans
// list Name column and after the create flow. Renders the SamplingPlanMatrix { segs, columns, cells } shape directly
// and mirrors the RA register page (band-coloured cells, likelihood<->health toggle, quant/qual indicators, meta
// strip) plus the plan-specific editable Planned #. Band colours resolve client-side from tenant risk-config via the
// shared risk-color helpers (no separate RA fetch). Also delivers Go-Bulk (row/column/cell select + bulk-apply), the
// Filters popup + applied-filter chips and the group-rollup cell. The rollup renders on AGENT cells that inherit
// Planned # from Agent-Group(s): editable DIRECT value + read-only LOCKED group-contribution chip + computed TOTAL,
// with the per-group breakdown on hover — fed by the DASH-2709 read-side fields (cell.groupContribution /
// groupContributions). The SEG Profile Summary rollup widget (AC5) is tracked separately.
@Component({
  standalone: false,
    selector: 'app-sample-planning-detail',
    templateUrl: './sample-planning-detail.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./sample-planning-detail.component.scss'],
})
export class SamplePlanningDetailComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    planId = '';
    matrix: SamplingPlanMatrix | null = null;
    segs: SamplingPlanMatrixSeg[] = [];
    columns: SamplingPlanMatrixColumn[] = [];

    // DASH-2757 (Issue 1): the parent Sampling Plan's own name is not on the matrix DTO, so it is fetched alongside the
    // matrix and shown/edited in the page header. `plan` holds the full editable plan so a name edit can be persisted
    // through the same generic Update the create/edit dialog uses (carrying every field, mirroring setStatus). planName
    // is the last-persisted value; nameDraft is the in-progress edit bound to the header input.
    plan: SamplingPlan | null = null;
    planName = '';
    nameDraft = '';
    isSavingName = false;
    // DASH-2757 (design review): the title is a static page identifier by default; a pencil affordance opens the inline
    // editor (this flag), keeping the header uncluttered instead of always showing a form control.
    isEditingName = false;

    riskLevels: RiskLevel[] = [];
    exposureLikelihoodLevels: LikelihoodColorLevel[] = [];

    useHealthRiskColoring = false;
    isLoading = false;
    isSaving = false;

    private _searchTerm = '';
    visibleColumns: SamplingPlanMatrixColumn[] = [];
    viewRows: RowViewModel[] = [];
    columnSelected: boolean[] = [];

    private cellMap = new Map<string, SamplingPlanMatrixCell>();
    private plannedEdits = new Map<string, number | null>();
    private originalPlanned = new Map<string, number | null>();
    private overrides = new Set<string>();

    // DASH-2706 Go-Bulk (item 2): bulk-select cells (row / column / individual) then apply one Planned # to all.
    bulkMode = false;
    bulkValue: number | null = null;
    private selected = new Set<string>();

    // DASH-2706 filters (item 3): a popup of cell filters; the active ones render as removable chips.
    filtersOpen = false;
    readonly filterOptions: MatrixFilterOption[] = [
        { id: 'quant', label: 'Quantitative', group: 'method', test: (c) => c?.methodOfAssessment === MethodOfAssessment.Quantitative },
        { id: 'qual', label: 'Qualitative', group: 'method', test: (c) => c?.methodOfAssessment === MethodOfAssessment.Qualitative },
        { id: 'assessed', label: 'Assessed', group: 'state', test: (c) => !!c?.isAssessed },
        { id: 'notAssessed', label: 'Not assessed', group: 'state', test: (c) => !c?.isAssessed },
        { id: 'hazardNa', label: 'Hazard N/A', group: 'state', test: (c) => !!c?.hazardDoesNotApply },
    ];
    private activeFilterIds = new Set<string>();
    appliedFilterChips: MatrixFilterOption[] = [];

    // DASH-2712 AC5: the SEG Profile Summary rollup rows, recomputed whenever Planned edits change.
    plannedRollups: PlannedRollupRow[] = [];

    readonly SamplingPlanStatus = SamplingPlanStatus;

    // DASH-3190 (item 1): the plan-detail page no longer renders the "Planning and Assessment" tab bar — it is a DETAIL
    // view reached from the Planning list and now matches its peer, the Risk Assessment detail page, which renders none.
    // The former `tabs` / `selectedTabIndex` inputs (DASH-2718 AC6 / DASH-2736) were removed with the tab bar.

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private samplingPlanService: SamplingPlanService,
        private tenantRiskService: TenantRiskService,
        private permissionService: PermissionService,
        private commonService: CommonService,
        private toastService: ToastService,
        private translateService: TranslateService,
        private jurisdictionRiskConfigService: JurisdictionRiskConfigService,
        private dialog: MatDialog,
    ) {}

    ngOnInit(): void {
        this.planId = this.route.snapshot.paramMap.get('id') ?? '';
        this.load();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    get isLocked(): boolean {
        return this.matrix?.status === SamplingPlanStatus.Approved || this.matrix?.acceptsEntryWrites === false;
    }

    // DASH-2742: the approval/revert actions mutate the plan, so they must be gated on the user's Update permission for
    // the SamplingPlan object — a Read-only user must not see (nor be able to trigger) them. Mirrors how the grid base
    // class derives canUpdate (GridPermissions.Update -> UpdateMy | UpdateHierarchy).
    get canUpdate(): boolean {
        return this.permissionService.hasPermissionFlag(
            RolePermissionObjects.SamplingPlan,
            PermissionFlags.UpdateMy | PermissionFlags.UpdateHierarchy,
        );
    }

    // DASH-2742 (G11v2 approve): the header Approve action is available on a Draft or Rejected plan (per AC) and hidden —
    // not merely disabled — otherwise, mirroring how Go Bulk drops out when locked. Also gated on Update permission.
    get canApprove(): boolean {
        return this.canUpdate
            && (this.matrix?.status === SamplingPlanStatus.Draft
                || this.matrix?.status === SamplingPlanStatus.Rejected);
    }

    // DASH-2742: the reverse Revert-to-Draft action shows only on an Approved plan so a demo can be re-run and a
    // mis-click is recoverable — the BE clears the approval stamp on the transition out of Approved. Also gated on Update.
    get canRevert(): boolean {
        return this.canUpdate && this.matrix?.status === SamplingPlanStatus.Approved;
    }

    // DASH-2757 (Issue 1): the name is editable only while the plan is unlocked (Draft/Rejected) AND the user has
    // Update permission — the same gate the matrix edits and approve/revert actions use, so the FE never invites an
    // edit the BE would reject. When locked it renders as read-only text.
    get canEditName(): boolean {
        return this.canUpdate && !this.isLocked;
    }

    // DASH-2757 (Issue 1): true once the draft differs from the persisted name and is non-empty — drives the inline
    // Save affordance and guards saveName() so a no-op / blank name is never posted.
    get hasNameChange(): boolean {
        const draft = (this.nameDraft ?? '').trim();
        return !!draft && draft !== (this.planName ?? '').trim();
    }

    // DASH-2757 (design review #8): a blank name is invalid — surfaces the inline "required" message and, together with
    // hasNameChange, keeps the confirm affordance disabled so the reason the edit cannot be committed is explicit
    // rather than a silently dead button.
    get hasValidName(): boolean {
        return !!(this.nameDraft ?? '').trim();
    }

    get hasReferenceRa(): boolean {
        return !!this.matrix?.referenceRiskAssessmentId;
    }

    // DASH-2712 (Part B / AC3) meta-strip: the pinned Reference RA name now comes from the matrix DTO (DASH-2711), so
    // it survives a direct load / reload instead of relying on fragile router state that was lost on refresh. Falls
    // back to a generic "Pinned" if a Reference RA is pinned but the BE returned no name; "Not assessed" when the plan
    // has no Reference RA. Translated in the template (the raw name passes through untranslated).
    get referenceRaName(): string | null {
        return this.matrix?.referenceRiskAssessmentName?.trim() || null;
    }

    get referenceRaLabel(): string {
        if (!this.hasReferenceRa) return 'Not assessed';
        return this.referenceRaName || 'Pinned';
    }

    get showReferenceRaName(): boolean {
        return this.hasReferenceRa && !!this.referenceRaName;
    }

    // DASH-2712 (Part B / AC3) meta-strip: the pinned Reference RA's Time Period, shown only when both bounds are
    // present. The ints are Unix timestamps (same shape the RA register meta-strip renders via the dateFormat pipe).
    get hasReferencePeriod(): boolean {
        return this.matrix?.referenceRiskAssessmentPeriodStart != null
            && this.matrix?.referenceRiskAssessmentPeriodEnd != null;
    }

    get hasChanges(): boolean {
        for (const [key, value] of this.plannedEdits) {
            if ((this.originalPlanned.get(key) ?? null) !== (value ?? null)) return true;
        }
        return false;
    }

    get searchTerm(): string {
        return this._searchTerm;
    }

    set searchTerm(value: string) {
        this._searchTerm = value ?? '';
        this.filterColumns();
    }

    private filterColumns(): void {
        const term = (this._searchTerm ?? '').trim().toLowerCase();
        this.visibleColumns = term
            ? this.columns.filter((c) => (c.name ?? '').toLowerCase().includes(term))
            : this.columns;
        this.buildViewRows();
    }

    private load(): void {
        this.isLoading = true;
        forkJoin({
            tenant: this.tenantRiskService.getCurrent(),
            matrix: this.samplingPlanService.getMatrix(this.planId),
            // DASH-2757 (Issue 1): the plan's name lives on the plan, not the matrix DTO — fetch it here so it survives
            // a direct load / reload and refreshes on every re-read (approve, save, name edit).
            plan: this.samplingPlanService.getById(this.planId),
        })
            .pipe(
                // Resolve bands/colours through the SAME override chain the RA register uses
                // (tenant base -> jurisdiction risk-config override -> qualitative labels), reading the
                // Reference RA's jurisdiction straight off the matrix DTO. Previously this page did only the
                // tenant step, so ~99% of cells fell back to the grey DEFAULT_SCORE_COLOR.
                switchMap(({ tenant, matrix, plan }) =>
                    resolveRiskConfig(
                        tenant,
                        matrix?.referenceRiskAssessmentJurisdictionId,
                        this.jurisdictionRiskConfigService,
                        { defaultColor: this.commonService.colors.greyLight },
                    ).pipe(map((resolved) => ({ matrix, plan, resolved }))),
                ),
                takeUntil(this.destroy$),
            )
            .subscribe({
                next: ({ matrix, plan, resolved }) => {
                    this.riskLevels = resolved.riskLevels;
                    this.exposureLikelihoodLevels = resolved.exposureLikelihoodLevels;
                    this.setPlan(plan);
                    this.setMatrix(matrix);
                    this.isLoading = false;
                },
                error: (error) => {
                    this.isLoading = false;
                    this.toastService.showError(error, 'Failed to load the sampling plan matrix');
                },
            });
    }

    private setMatrix(matrix: SamplingPlanMatrix): void {
        this.matrix = matrix;
        this.segs = matrix?.segs ?? [];
        this.columns = matrix?.columns ?? [];
        this.cellMap.clear();
        this.plannedEdits.clear();
        this.originalPlanned.clear();
        this.selected.clear();
        // DASH-2706 M1 + stale-override fix: reset `overrides` and re-derive it purely from the persisted data.
        // setMatrix() runs on both load() and save() re-read. The M1 bug was clearing `overrides` and NOT
        // re-deriving — that wiped the marker on an overridden HazardDoesNotApply cell right after saving its
        // Planned #, flipping `isHazardNa` back to true and hiding the value just persisted. Re-deriving fixes M1
        // (a HazardDoesNotApply cell that carries a persisted Planned # can only be there because it was
        // overridden, so the marker survives both the save round-trip and a full reload) while the up-front clear
        // eliminates stale override keys leaking across plan switches when Angular reuses the component instance.
        this.overrides.clear();
        (matrix?.cells ?? []).forEach((cell) => {
            const key = this.keyFor(cell.segId, cell.agentId, cell.agentGroupId);
            this.cellMap.set(key, cell);
            this.plannedEdits.set(key, cell.plannedNumberOfSamples ?? null);
            this.originalPlanned.set(key, cell.plannedNumberOfSamples ?? null);
            if (cell.hazardDoesNotApply && cell.plannedNumberOfSamples != null) {
                this.overrides.add(key);
            }
        });
        this.filterColumns();
    }

    // DASH-2757 (Issue 1): capture the editable plan and reset the header name/draft from the persisted value. Runs on
    // load() and after every re-read (approve/revert re-load, matrix save re-read, name save) so the header name always
    // reflects the latest server state and a stale draft never lingers.
    private setPlan(plan: SamplingPlan): void {
        this.plan = plan;
        this.planName = plan?.name ?? '';
        this.nameDraft = this.planName;
        // Any fresh server read supersedes an in-progress edit, so leave/abandon the inline editor and fall back to the
        // static title — this also relocks the name the moment an approve/revert re-read makes the plan non-editable.
        this.isEditingName = false;
    }

    // DASH-2757 (design review #1/#3): open the inline editor from the static title. Seed the draft from the persisted
    // name so the field starts clean; guarded so a locked / no-permission plan can never enter edit mode.
    startEditName(): void {
        if (!this.canEditName) return;
        this.nameDraft = this.planName;
        this.isEditingName = true;
    }

    // DASH-2757 (design review #8): revert the draft to the persisted name and return to the static title (Esc / cancel
    // icon) so an abandoned edit never lingers.
    cancelEditName(): void {
        this.nameDraft = this.planName;
        this.isEditingName = false;
    }

    // DASH-2757 (Issue 1): persist a header name edit through the SAME generic Update the create/edit dialog and
    // setStatus() use — read-modify-write the full editable plan so every field is carried (a partial payload could be
    // read as "not provided"). Guarded on hasNameChange + permission/lock so a blank/unchanged name or a locked plan
    // never posts. On success the plan is re-read so planName/draft resync; on error the draft reverts to the persisted
    // name so the FE and BE stay consistent.
    saveName(): void {
        if (!this.plan || !this.canEditName || !this.hasNameChange || this.isSavingName) return;
        const name = this.nameDraft.trim();
        this.isSavingName = true;
        this.samplingPlanService
            .update({ ...this.plan, name })
            .pipe(
                switchMap(() => this.samplingPlanService.getById(this.planId)),
                takeUntil(this.destroy$),
            )
            .subscribe({
                next: (plan) => {
                    this.setPlan(plan);
                    this.isSavingName = false;
                    this.toastService.showSuccess('Sampling plan name updated', true);
                },
                error: (error) => {
                    this.isSavingName = false;
                    this.nameDraft = this.planName;
                    this.toastService.showError(error, 'Failed to update the sampling plan name');
                },
            });
    }

    get selectedCount(): number {
        return this.selected.size;
    }

    private buildViewRows(): void {
        this.columnSelected = this.visibleColumns.map((column) => this.isColumnFullySelected(column));
        this.viewRows = this.segs
            .map((seg) => {
                const cells = this.visibleColumns.map((column) => this.buildCellVm(seg, column));
                return {
                    seg,
                    cells,
                    selected: this.isRowFullySelected(cells),
                };
            })
            // DASH-2706 filters (item 3): drop rows that have no cell matching the active filters.
            .filter((row) => !this.activeFilterIds.size || row.cells.some((vm) => !vm.filteredOut));
        // DASH-2712 AC5: keep the SEG Profile Summary in sync with load / filter / toggle / bulk changes. It reads all
        // columns/SEGs directly, so it is unaffected by the active filters.
        this.buildRollupSummary();
    }

    private buildCellVm(seg: SamplingPlanMatrixSeg, column: SamplingPlanMatrixColumn): CellViewModel {
        const cell = this.getCell(seg, column);
        const overridden = this.isOverridden(seg, column);
        const key = this.cellKey(seg, column);
        const planned = this.getPlanned(seg, column);
        const contribution = this.groupContribution(cell);
        const isRollup = !column.isGroup && (cell?.groupContribution != null || !!cell?.groupContributions?.length);
        return {
            seg,
            column,
            cell,
            color: this.getCellColor(cell),
            notAssessed: !cell?.isAssessed,
            isHazardNa: !!(cell?.hazardDoesNotApply && !overridden),
            isOverridden: overridden,
            isQuant: cell?.methodOfAssessment === MethodOfAssessment.Quantitative,
            isQual: cell?.methodOfAssessment === MethodOfAssessment.Qualitative,
            bandLabel: this.getCellBandLabel(cell),
            plannable: this.isPlannable(cell, seg, column),
            planned,
            showEnable: !this.isLocked,
            filteredOut: !this.passesFilters(cell),
            selected: this.selected.has(key),
            isGroupCol: !!column.isGroup,
            isRollup,
            contribution,
            total: (planned ?? 0) + contribution,
            rollupTitle: this.rollupTitle(cell),
        };
    }

    // DASH-2706 filters (item 3): options in the same group OR together, groups AND together.
    private passesFilters(cell: SamplingPlanMatrixCell | undefined): boolean {
        if (!this.activeFilterIds.size) return true;
        const active = this.filterOptions.filter((o) => this.activeFilterIds.has(o.id));
        const groups = Array.from(new Set(active.map((o) => o.group)));
        return groups.every((group) =>
            active.filter((o) => o.group === group).some((o) => o.test(cell)),
        );
    }

    // DASH-2709: the Planned # this AGENT cell inherits from the Agent-Group(s) it belongs to (server-computed, a
    // fixed read-side value that only refreshes on the save re-read). Null/0 for group columns and agents that
    // inherit nothing — the LOCKED contribution chip + TOTAL fall back to the direct value.
    private groupContribution(cell: SamplingPlanMatrixCell | undefined): number {
        return cell?.groupContribution ?? 0;
    }

    // DASH-2709 group-rollup hover label: 'incl. N from {group}, M from {group2}' from the per-group breakdown.
    private rollupTitle(cell: SamplingPlanMatrixCell | undefined): string {
        return this.rollupTitleFrom(cell?.groupContributions);
    }

    // DASH-2709 / DASH-2712 AC2: build the group-named hover label from a per-group contribution breakdown, reused by
    // the in-cell rollup (single cell) and the SEG Profile Summary (contributions aggregated per group across SEGs).
    // All strings route through i18n so no raw English is concatenated in TS.
    private rollupTitleFrom(contributions: SamplingPlanMatrixGroupContribution[] | null | undefined): string {
        const parts = (contributions ?? [])
            .filter((g) => g && g.plannedNumberOfSamples)
            .map((g) =>
                this.translateService.getDynamicTranslation('{{n}} from {{group}}', {
                    n: g.plannedNumberOfSamples,
                    group: g.agentGroupName,
                }),
            );
        return parts.length
            ? this.translateService.getDynamicTranslation('incl. {{contributions}}', { contributions: parts.join(', ') })
            : '';
    }

    // DASH-2712 AC5: recompute the SEG Profile Summary from ALL columns/SEGs (filter-independent — it summarises the
    // whole plan). An agent's DIRECT is the sum of its own Planned across SEGs; its CONTRIBUTION is the sum of the
    // DASH-2709 group-contribution across SEGs, with the per-group breakdown aggregated per group for the hover text
    // (AC2: an agent in multiple groups shows the summed contribution with per-group names on hover). A group column
    // stays a SEPARATE line carrying only its own DIRECT total (never summed into a member agent's row).
    private buildRollupSummary(): void {
        this.plannedRollups = this.columns.map((column) => {
            let direct = 0;
            let contribution = 0;
            const breakdown = new Map<string, SamplingPlanMatrixGroupContribution>();
            for (const seg of this.segs) {
                direct += this.getPlanned(seg, column) ?? 0;
                if (column.isGroup) continue;
                const cell = this.getCell(seg, column);
                contribution += cell?.groupContribution ?? 0;
                for (const g of cell?.groupContributions ?? []) {
                    if (!g || !g.plannedNumberOfSamples) continue;
                    const existing = breakdown.get(g.agentGroupId);
                    if (existing) existing.plannedNumberOfSamples += g.plannedNumberOfSamples;
                    else breakdown.set(g.agentGroupId, { ...g });
                }
            }
            return {
                columnName: column.name,
                isGroup: !!column.isGroup,
                direct,
                contribution,
                total: direct + contribution,
                contributionLabel: contribution
                    ? this.translateService.getDynamicTranslation('incl. {{n}} from group(s)', { n: contribution })
                    : '',
                rollupTitle: this.rollupTitleFrom(Array.from(breakdown.values())),
            };
        });
    }

    private refreshCellVm(vm: CellViewModel): void {
        Object.assign(vm, this.buildCellVm(vm.seg, vm.column));
    }

    private keyFor(segId: string, agentId: string | null, agentGroupId: string | null): string {
        return `${segId}|${agentGroupId != null ? 'g' + agentGroupId : 'a' + agentId}`;
    }

    cellKey(seg: SamplingPlanMatrixSeg, column: SamplingPlanMatrixColumn): string {
        return this.keyFor(seg.segId, column.agentId, column.agentGroupId);
    }

    getCell(seg: SamplingPlanMatrixSeg, column: SamplingPlanMatrixColumn): SamplingPlanMatrixCell | undefined {
        return this.cellMap.get(this.cellKey(seg, column));
    }

    getPlanned(seg: SamplingPlanMatrixSeg, column: SamplingPlanMatrixColumn): number | null {
        return this.plannedEdits.get(this.cellKey(seg, column)) ?? null;
    }

    setPlanned(seg: SamplingPlanMatrixSeg, column: SamplingPlanMatrixColumn, value: number | null): void {
        const key = this.cellKey(seg, column);
        const normalized = value == null || (value as unknown as string) === '' || value < 0 ? null : value;
        this.plannedEdits.set(key, normalized);
    }

    isOverridden(seg: SamplingPlanMatrixSeg, column: SamplingPlanMatrixColumn): boolean {
        return this.overrides.has(this.cellKey(seg, column));
    }

    isPlannable(cell: SamplingPlanMatrixCell | undefined, seg: SamplingPlanMatrixSeg, column: SamplingPlanMatrixColumn): boolean {
        if (this.isLocked) return false;
        if (cell?.hazardDoesNotApply && !this.isOverridden(seg, column)) return false;
        return true;
    }

    enableOverride(seg: SamplingPlanMatrixSeg, column: SamplingPlanMatrixColumn): void {
        if (this.isLocked) return;
        this.overrides.add(this.cellKey(seg, column));
    }

    getCellColor(cell: SamplingPlanMatrixCell | undefined): string {
        if (!cell || !cell.isAssessed || cell.hazardDoesNotApply) return '';
        if (this.useHealthRiskColoring) {
            return getRiskColor(cell.healthRiskScore ?? 0, this.riskLevels);
        }
        const score = cell.quantitativeLikelihoodScore ?? cell.qualitativeLikelihoodScore ?? 0;
        return getLikelihoodColor(score, this.exposureLikelihoodLevels);
    }

    /**
     * Exposure band label for a cell, e.g. "High (20-40% OEL)". Resolved by assessment method so it
     * matches the RA register for the same SEG/agent pair — qualitative cells off
     * `qualitativeLikelihoodScore` + `Name`, quantitative cells off `quantitativeLikelihoodScore` +
     * `QuantitativeName`. Returns null for unassessed / hazard-N/A / unmatched cells so the template
     * renders "Not assessed" instead of a bare coloured box.
     */
    getCellBandLabel(cell: SamplingPlanMatrixCell | undefined): string | null {
        if (!cell || !cell.isAssessed || cell.hazardDoesNotApply) return null;
        const isQual = cell.methodOfAssessment === MethodOfAssessment.Qualitative;
        const score = isQual
            ? cell.qualitativeLikelihoodScore ?? 0
            : cell.quantitativeLikelihoodScore ?? 0;
        return getLikelihoodBandLabel(score, this.exposureLikelihoodLevels, isQual);
    }

    toggleColoringMode(): void {
        this.useHealthRiskColoring = !this.useHealthRiskColoring;
        this.buildViewRows();
    }

    onEnableOverride(vm: CellViewModel): void {
        this.enableOverride(vm.seg, vm.column);
        this.refreshCellVm(vm);
    }

    onPlannedChange(vm: CellViewModel, value: number | null): void {
        this.setPlanned(vm.seg, vm.column, value);
        vm.planned = this.getPlanned(vm.seg, vm.column);
        if (vm.isRollup) {
            // DASH-2709: an agent's DIRECT edit changes its own TOTAL (= direct + fixed group contribution). Patch
            // the TOTAL chip in-place so it doesn't show the stale value (contribution only refreshes on save re-read).
            this.refreshCellVm(vm);
        }
        // DASH-2712 AC5: a DIRECT edit shifts the SEG Profile Summary DIRECT/TOTAL for that column — recompute it.
        this.buildRollupSummary();
    }

    // ---- DASH-2706 Go-Bulk (item 2) ---------------------------------------------------------------------------
    private isSelectable(vm: CellViewModel): boolean {
        return vm.plannable && !vm.filteredOut;
    }

    private isRowFullySelected(cells: CellViewModel[]): boolean {
        const selectable = cells.filter((vm) => this.isSelectable(vm));
        return selectable.length > 0 && selectable.every((vm) => this.selected.has(this.cellKey(vm.seg, vm.column)));
    }

    private isColumnFullySelected(column: SamplingPlanMatrixColumn): boolean {
        let any = false;
        for (const seg of this.segs) {
            const vm = this.buildCellVm(seg, column);
            if (!this.isSelectable(vm)) continue;
            any = true;
            if (!this.selected.has(this.cellKey(seg, column))) return false;
        }
        return any;
    }

    toggleBulkMode(): void {
        this.bulkMode = !this.bulkMode;
        if (!this.bulkMode) {
            this.selected.clear();
            this.bulkValue = null;
        }
        this.buildViewRows();
    }

    toggleCellSelect(vm: CellViewModel): void {
        if (!this.isSelectable(vm)) return;
        const key = this.cellKey(vm.seg, vm.column);
        if (this.selected.has(key)) this.selected.delete(key);
        else this.selected.add(key);
        this.buildViewRows();
    }

    toggleRowSelect(row: RowViewModel): void {
        const selectable = row.cells.filter((vm) => this.isSelectable(vm));
        const select = !row.selected;
        selectable.forEach((vm) => {
            const key = this.cellKey(vm.seg, vm.column);
            if (select) this.selected.add(key);
            else this.selected.delete(key);
        });
        this.buildViewRows();
    }

    toggleColumnSelect(columnIndex: number): void {
        const column = this.visibleColumns[columnIndex];
        if (!column) return;
        const select = !this.columnSelected[columnIndex];
        this.segs.forEach((seg) => {
            const vm = this.buildCellVm(seg, column);
            if (!this.isSelectable(vm)) return;
            const key = this.cellKey(seg, column);
            if (select) this.selected.add(key);
            else this.selected.delete(key);
        });
        this.buildViewRows();
    }

    applyBulk(): void {
        if (this.isLocked || !this.selected.size) return;
        const value = this.bulkValue == null || (this.bulkValue as unknown as string) === '' || this.bulkValue < 0
            ? null
            : this.bulkValue;
        this.selected.forEach((key) => this.plannedEdits.set(key, value));
        this.buildViewRows();
    }

    // ---- DASH-2706 Filters (item 3) ---------------------------------------------------------------------------
    toggleFiltersPopup(): void {
        this.filtersOpen = !this.filtersOpen;
    }

    isFilterActive(id: string): boolean {
        return this.activeFilterIds.has(id);
    }

    toggleFilter(id: string): void {
        if (this.activeFilterIds.has(id)) this.activeFilterIds.delete(id);
        else this.activeFilterIds.add(id);
        this.refreshFilters();
    }

    removeFilter(id: string): void {
        this.activeFilterIds.delete(id);
        this.refreshFilters();
    }

    clearFilters(): void {
        this.activeFilterIds.clear();
        this.refreshFilters();
    }

    private refreshFilters(): void {
        this.appliedFilterChips = this.filterOptions.filter((o) => this.activeFilterIds.has(o.id));
        this.buildViewRows();
    }

    goBack(): void {
        this.router.navigate([RoutePaths.samplingPlans]);
    }

    // DASH-2742 (G11v2 approve): confirm, then flip the plan to Approved. Approving locks the matrix, so it must not
    // be a single mis-click — the confirmation step is required by AC. On success the matrix is re-read (no page
    // reload) so the status badge, lock banner and read-only matrix all reflect the new state.
    approve(): void {
        if (!this.canApprove) return;
        this.changeStatus(
            SamplingPlanStatus.Approved,
            this.translateService.getDynamicTranslation('Approve Sampling Plan'),
            this.translateService.getDynamicTranslation('Approving this plan locks its matrix against further edits. Continue?'),
            this.translateService.getDynamicTranslation('Approve'),
            this.translateService.getDynamicTranslation('Sampling plan approved'),
        );
    }

    // DASH-2742: reverse the approval back to Draft so an approved plan is not permanently frozen (the BE clears the
    // approval stamp on the transition out). Confirmed too, since it re-opens the matrix for editing.
    revertToDraft(): void {
        if (!this.canRevert) return;
        this.changeStatus(
            SamplingPlanStatus.Draft,
            this.translateService.getDynamicTranslation('Revert Sampling Plan to Draft'),
            this.translateService.getDynamicTranslation('Reverting this plan to Draft unlocks its matrix for editing and clears the approval. Continue?'),
            this.translateService.getDynamicTranslation('Revert to Draft'),
            this.translateService.getDynamicTranslation('Sampling plan reverted to Draft'),
        );
    }

    private changeStatus(status: SamplingPlanStatus, title: string, message: string, primaryButtonText: string, successMessage: string): void {
        const data: ConfirmDialogData = {
            title,
            message,
            primaryButtonText,
            primaryCallback: () => this.samplingPlanService.setStatus(this.planId, status),
            successMessage,
            secondaryButtonText: this.translateService.getDynamicTranslation('Cancel'),
            errorMessage: this.translateService.getDynamicTranslation('Failed to update the sampling plan status'),
        };
        this.dialog
            .open(ConfirmDialogComponent, { data, width: '450px', disableClose: true })
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((result) => {
                // DASH-2757 (design review #11): the confirm dialog closes with `false` on cancel and with the Update
                // *response* on a confirmed success. SamplingPlan/Update returns an empty body, so that success value is
                // falsy — the previous `if (result)` therefore never re-read after Approve / Revert to Draft and the
                // header, lock banner and read-only matrix stayed stale until a manual reload. Re-read on anything that
                // is not an explicit cancel (`!== false`) so a confirmed transition always refreshes in place. (The
                // dialog uses disableClose, so backdrop/Esc cannot resolve it to undefined.)
                if (result !== false) this.load();
            });
    }

    save(): void {
        if (this.isLocked || !this.hasChanges || this.isSaving) return;
        const cells: SamplingPlanMatrixWriteCell[] = [];
        for (const seg of this.segs) {
            for (const column of this.columns) {
                const key = this.cellKey(seg, column);
                const value = this.plannedEdits.get(key) ?? null;
                if ((this.originalPlanned.get(key) ?? null) === value) continue;
                cells.push({
                    segId: seg.segId,
                    agentId: column.agentId,
                    agentGroupId: column.agentGroupId,
                    plannedNumberOfSamples: value,
                });
            }
        }
        if (!cells.length) return;

        this.isSaving = true;
        this.samplingPlanService
            .saveMatrix(this.planId, { cells })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (matrix) => {
                    this.setMatrix(matrix);
                    this.isSaving = false;
                    this.toastService.showSuccess('Sampling plan saved', true);
                },
                error: (error) => {
                    this.isSaving = false;
                    this.toastService.showError(error, 'Failed to save the sampling plan');
                    // DASH-2757 (Issue 2): a save is rejected when the plan has since been approved/locked (the BE
                    // blocks Number-of-Samples edits on an Approved plan). The FE had no way to reach that state, so it
                    // kept presenting the fields as editable. Re-read the plan + matrix so the header, lock banner and
                    // read-only matrix reflect the true server state — the FE and BE agree on what is editable again.
                    this.load();
                },
            });
    }

    trackByIndex(index: number): number {
        return index;
    }
}
